import { createEmptyScene } from './schema';
import { migrateFieldConfigToConstraints } from './fieldConfigMigration';
import { layoutSceneGraph } from '../typography/layoutText';
import { createMeasureFn, clearMeasureCache } from '../typography/measureText';
import {
  extractGcpObjectKeyFromSrc,
  extractGcpObjectKeyFromGcsUrl,
  gcpImageStreamUrl,
  resolveImageObjectKey,
} from '../api/gcpImagesApi';

function ensureTextAutoGrow(node) {
  if (node?.type !== 'text') return node;
  const c = node.constraints || migrateFieldConfigToConstraints(node.fieldConfig);
  if (c.layoutMode === 'fixedFrame') return { ...node, constraints: c };
  if (c.overflowStrategy === 'wrap' && c.layoutMode !== 'autoWidth' && c.layoutMode !== 'scaleDown') {
    return { ...node, constraints: { ...c, layoutMode: 'autoHeight', overflowStrategy: 'wrap' } };
  }
  return { ...node, constraints: c };
}

let nodeIdSeq = 0;
function nextId(prefix) {
  nodeIdSeq += 1;
  return `${prefix}_${nodeIdSeq}`;
}

function fabricColorToCss(fill) {
  if (!fill) return '#111827';
  if (typeof fill === 'string') return fill;
  return '#111827';
}

function typographyFromFabric(obj) {
  return {
    fontFamily: obj.fontFamily || 'Arial',
    fontSize: Number(obj.fontSize) || 12,
    fontWeight: obj.fontWeight || 'normal',
    fontStyle: obj.fontStyle || 'normal',
    lineHeight: Number(obj.lineHeight) > 0 ? Number(obj.lineHeight) : 1.16,
    letterSpacing: Number(obj.charSpacing) || 0,
    textAlign: obj.textAlign || 'left',
    fill: fabricColorToCss(obj.fill),
    underline: Boolean(obj.underline),
  };
}

function transformFromBoundingRect(obj, br) {
  return {
    x: br.left,
    y: br.top,
    width: Math.max(1, br.width),
    height: Math.max(1, br.height),
    rotation: Number(obj.angle) || 0,
    scaleX: 1,
    scaleY: 1,
    originX: 'left',
    originY: 'top',
    opacity: obj.opacity ?? 1,
  };
}

function pushTextNode(scene, obj, br, clip) {
  const pad = 2;
  scene.objects.push({
    id: obj.id || nextId('text'),
    type: 'text',
    zIndex: scene.objects.length,
    fieldKey: obj.fieldKey || obj.get?.('fieldKey'),
    fieldConfig: obj.fieldConfig || obj.get?.('fieldConfig'),
    text: String(obj.text ?? ''),
    transform: transformFromBoundingRect(obj, br),
    typography: typographyFromFabric(obj),
    constraints: migrateFieldConfigToConstraints(obj.fieldConfig || obj.get?.('fieldConfig')),
    clip,
    fabricType: obj.type,
  });
}

function pushImageNode(scene, obj, br) {
  let src = '';
  try {
    src = obj._originalElement?.src || obj.getSrc?.() || obj.src || '';
    if (!src && typeof obj.toDataURL === 'function') {
      src = obj.toDataURL({ format: 'png' });
    }
  } catch {
    src = obj.src || '';
  }
  scene.objects.push({
    id: obj.id || nextId('img'),
    type: 'image',
    zIndex: scene.objects.length,
    fieldKey: obj.fieldKey || obj.get?.('fieldKey'),
    fieldConfig: obj.fieldConfig,
    src,
    transform: transformFromBoundingRect(obj, br),
    fabricType: 'image',
  });
}

function pushShapeNode(scene, obj, br, shapeId) {
  scene.objects.push({
    id: shapeId || obj.id || nextId('shape'),
    type: 'shape',
    zIndex: scene.objects.length,
    fieldKey: obj.fieldKey,
    fill: fabricColorToCss(obj.fill),
    stroke: typeof obj.stroke === 'string' ? obj.stroke : undefined,
    strokeWidth: Number(obj.strokeWidth) || 0,
    transform: transformFromBoundingRect(obj, br),
    fabricType: obj.type,
  });
  return shapeId || obj.id || nextId('shape');
}

function isTextFabricType(type) {
  return type === 'textbox' || type === 'i-text' || type === 'text';
}

function walkFabricObject(scene, obj, clipRegionId) {
  if (!obj) return;
  try {
    obj.setCoords?.();
  } catch {
    /* ignore */
  }

  let br;
  try {
    br = obj.getBoundingRect(true);
  } catch {
    br = { left: obj.left || 0, top: obj.top || 0, width: obj.width || 100, height: obj.height || 40 };
  }

  if (isTextFabricType(obj.type)) {
    pushTextNode(scene, obj, br, clipRegionId ? { regionId: clipRegionId, shapeType: 'rect' } : undefined);
    return;
  }

  if (obj.type === 'image') {
    pushImageNode(scene, obj, br);
    return;
  }

  if (obj.type === 'group') {
    const isShapeLabel = !!(obj._shapeLabelGroup || obj.get?.('_shapeLabelGroup'));
    const isTextBox = !!(obj._textBoxGroup || obj.get?.('_textBoxGroup'));
    const children = typeof obj.getObjects === 'function' ? obj.getObjects() : [];
    if (isTextBox) {
      const labelPart = children.find((c) => isTextFabricType(c.type));
      if (labelPart) {
        try {
          labelPart.setCoords?.();
          pushTextNode(scene, labelPart, labelPart.getBoundingRect(true));
        } catch {
          /* skip */
        }
      }
      return;
    }
    if (isShapeLabel) {
      const shapePart = children.find((c) => !isTextFabricType(c.type)) || children[0];
      const labelPart = children.find((c) => isTextFabricType(c.type));
      let regionId = clipRegionId;
      if (shapePart) {
        try {
          shapePart.setCoords?.();
          const sbr = shapePart.getBoundingRect(true);
          regionId = pushShapeNode(scene, shapePart, sbr);
        } catch {
          regionId = pushShapeNode(scene, obj, br);
        }
      }
      if (labelPart) {
        try {
          labelPart.setCoords?.();
          pushTextNode(scene, labelPart, labelPart.getBoundingRect(true), {
            regionId: regionId || nextId('clip'),
            shapeType: 'rect',
          });
        } catch {
          /* skip */
        }
      }
      return;
    }
    for (const child of children) walkFabricObject(scene, child, clipRegionId);
    return;
  }

  if (obj.type === 'rect') {
    const isTextFrame = !!(obj._textBoxFrame || obj.get?.('_textBoxFrame'));
    if (!isTextFrame) pushShapeNode(scene, obj, br);
    return;
  }

  if (['circle', 'triangle', 'polygon', 'path', 'ellipse', 'line'].includes(obj.type)) {
    pushShapeNode(scene, obj, br);
  }
}

/** Live Fabric canvas → scene graph (post-mapping export). */
export function fabricCanvasToSceneGraph(canvas, options = {}) {
  nodeIdSeq = 0;
  const w = Math.max(1, options.width || canvas?.getWidth?.() || 800);
  const h = Math.max(1, options.height || canvas?.getHeight?.() || 600);
  const scene = createEmptyScene(w, h);
  const bg = canvas?.backgroundColor;
  if (typeof bg === 'string') scene.background = bg;

  if (!canvas?.getObjects) return scene;
  for (const obj of canvas.getObjects()) {
    walkFabricObject(scene, obj, null);
  }
  return scene;
}

/**
 * Apply broker mappings onto scene graph nodes (text and image) and re-wrap lines for text.
 * @param {object} [options]
 * @param {boolean} [options.forExport] keep fixed frames; caller runs full PDF relayout
 * @param {boolean} [options.skipLayout] skip layoutSceneGraph (used before prepareSceneForPdfExport)
 */
export function applyMappingsToSceneGraph(scene, mappings, lookupFn, options = {}) {
  if (!scene?.objects || !mappings) return scene;
  const forExport = Boolean(options.forExport);
  for (const node of scene.objects) {
    if (!node.fieldKey) continue;
    const value = lookupFn ? lookupFn(mappings, node.fieldKey) : mappings[node.fieldKey];
    if (value === undefined || value === null) continue;

    if (node.type === 'image') {
      const url = String(value).trim();
      if (!url) continue;
      const key = resolveImageObjectKey(url);
      if (key) {
        node.objectKey = key;
        node.gcpObjectKey = key;
        node.src = gcpImageStreamUrl(key);
      } else {
        node.src = url;
        const fromSrc = extractGcpObjectKeyFromSrc(url) || extractGcpObjectKeyFromGcsUrl(url);
        if (fromSrc) {
          node.objectKey = fromSrc;
          node.gcpObjectKey = fromSrc;
        }
      }
      continue;
    }

    if (node.type !== 'text') continue;
    node.text = String(value);
    delete node.layout;
    if (!forExport) {
      const grown = ensureTextAutoGrow(node);
      Object.assign(node, grown);
    }
  }
  if (options.skipLayout) return scene;
  clearMeasureCache();
  return layoutSceneGraph(scene, createMeasureFn(true));
}
